import React, { useState, useCallback, useEffect } from 'react';
import { Button, CardComponent, AlertComponent, InputComponent } from '../../../components/shared';
import { authActions } from '../../../state/features/authReducer';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../state/features/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

export interface MFATOTPChallengeProps {
    onComplete: () => void;
    onCancel: () => void;
    username: string;
    sessionToken: string;
}

export const MFATOTPChallenge: React.FC<MFATOTPChallengeProps> = ({ 
    onComplete, 
    onCancel, 
    username,
    sessionToken
}) => {
    const [totpCode, setTotpCode] = useState<string>('');
    
    const { t } = useTranslation();
    const errorMessage = useSelector((x: RootState) => x.auth.error);
    const authLoading = useSelector((x: RootState) => x?.auth?.loading);
    const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

    const onSubmit = useCallback(async () => {
        if (totpCode.length >= 6) {
            dispatch(authActions.respondToTOTPMFA({
                username,
                sessionToken: sessionToken,
                code: totpCode.trim()
            }));
        }
    }, [totpCode, username, sessionToken, dispatch]);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            onSubmit();
        }
    }, [onSubmit]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const clickGate = Boolean(authLoading);

    return (
        <CardComponent title={t('mfa.title')} className='login-card'>
            {errorMessage && <AlertComponent className='alertText' message={errorMessage} type='error' />}
            
            <div style={{ marginBottom: '20px', textAlign: 'center', color: 'white' }}>
                <p>{t('mfa.enterSixDigitCodeFromApp')}</p>
            </div>

            <div className='fieldView'>
                <label className='labelView'>{t('auth.verificationCode')}</label>
                <InputComponent
                    value={totpCode}
                    placeholder={t('auth.enterCodeFromAuthenticator')}
                    onChangeEvent={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                        if (value.length <= 6) {
                            setTotpCode(value);
                        }
                    }}
                    disabled={clickGate}
                    maxLength={6}
                />
            </div>

            <div className='signBtnView'>
                <Button
                    type='primary'
                    onClickEvent={onSubmit}
                    disabled={clickGate || totpCode.length < 6}
                    className='SignBtn'
                >
                    {t('auth.verifyCode')}
                </Button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <Button
                    type='link'
                    onClickEvent={onCancel}
                    className='forgetPassword'
                >
                    {t('mfa.cancel')}
                </Button>
            </div>
        </CardComponent>
    );
};
